/**
 * The API client.
 *
 * Two behaviours worth knowing about:
 *
 * 1. **Transparent refresh.** A 401 carrying `token_expired` triggers one
 *    refresh and one replay of the original request. Concurrent 401s share a
 *    single refresh promise, so a dashboard firing six requests at once does
 *    not rotate the refresh token six times — which would invalidate itself.
 *
 * 2. **Typed failures.** Every non-2xx response becomes an `ApiRequestError`
 *    carrying the backend's stable `code`, so callers branch on that rather
 *    than string-matching prose.
 */

import type { ApiError } from '@/types/api'

const BASE = '/api/v1'

export class ApiRequestError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown>

  constructor(status: number, payload: ApiError) {
    super(payload.message)
    this.name = 'ApiRequestError'
    this.status = status
    this.code = payload.code
    this.details = payload.details ?? {}
  }

  /** Field-level validation errors, when the backend returned any. */
  get fieldErrors(): { field: string; message: string }[] {
    const errors = this.details.errors
    if (!Array.isArray(errors)) return []
    return errors as { field: string; message: string }[]
  }

  get isAuthError() {
    return this.status === 401
  }

  get isPermissionError() {
    return this.status === 403
  }

  /** The bot or the model is down — the console should degrade, not error out. */
  get isUnavailable() {
    return this.status === 503
  }
}

type Listener = () => void
const sessionLostListeners = new Set<Listener>()

/** Notified when the session cannot be recovered and the user must sign in. */
export function onSessionLost(listener: Listener): () => void {
  sessionLostListeners.add(listener)
  return () => sessionLostListeners.delete(listener)
}

function announceSessionLost() {
  sessionLostListeners.forEach((listener) => listener())
}

let refreshPromise: Promise<boolean> | null = null

async function refreshSession(): Promise<boolean> {
  // Collapse concurrent refreshes: the refresh token rotates on use, so two
  // parallel refreshes would invalidate each other.
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })
      return response.ok
    } catch {
      return false
    } finally {
      // Cleared on the next tick so callers awaiting this promise all observe
      // the same result before a new refresh can start.
      setTimeout(() => {
        refreshPromise = null
      }, 0)
    }
  })()

  return refreshPromise
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  query?: Record<string, string | number | boolean | string[] | undefined | null>
  /** Set false for the auth endpoints themselves, to avoid a refresh loop. */
  retryOnExpiry?: boolean
  raw?: boolean
}

function buildUrl(path: string, query?: RequestOptions['query']) {
  const url = `${BASE}${path}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)))
    } else {
      params.append(key, String(value))
    }
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const payload = await response.json()
    if (payload && typeof payload.code === 'string') return payload as ApiError
    return { code: 'http_error', message: payload?.message ?? response.statusText }
  } catch {
    return { code: 'http_error', message: response.statusText || 'Request failed' }
  }
}

async function execute(path: string, options: RequestOptions): Promise<Response> {
  const { body, query, retryOnExpiry: _r, raw: _raw, headers, ...rest } = options

  const init: RequestInit = {
    ...rest,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(headers ?? {}),
    },
  }
  // FormData carries its own multipart boundary, which only the browser can
  // generate. Declaring a Content-Type for it produces a request no server can
  // parse, so the header is dropped along with the JSON encoding.
  if (body instanceof FormData) {
    const headers = init.headers as Record<string, string>
    delete headers['Content-Type']
    init.body = body
  } else if (body !== undefined) {
    init.body = JSON.stringify(body)
  }

  return fetch(buildUrl(path, query), init)
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const retryOnExpiry = options.retryOnExpiry ?? true

  let response = await execute(path, options)

  if (response.status === 401 && retryOnExpiry) {
    const error = await parseError(response.clone())
    if (error.code === 'token_expired' || error.code === 'missing_token') {
      const refreshed = await refreshSession()
      if (refreshed) {
        response = await execute(path, options)
      } else {
        announceSessionLost()
        throw new ApiRequestError(401, error)
      }
    }
  }

  if (!response.ok) {
    const error = await parseError(response)
    if (response.status === 401) announceSessionLost()
    throw new ApiRequestError(response.status, error)
  }

  if (options.raw) return response as unknown as T
  if (response.status === 204) return undefined as T

  const text = await response.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  upload: <T>(path: string, form: FormData, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body: form }),
  delete: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE', body }),

  /** Download a file response, returning a blob and its filename. */
  async download(
    path: string,
    options?: RequestOptions,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await request<Response>(path, { ...options, method: 'GET', raw: true })
    const disposition = response.headers.get('Content-Disposition') ?? ''
    const match = /filename="?([^"]+)"?/.exec(disposition)
    return { blob: await response.blob(), filename: match?.[1] ?? 'download' }
  },
}

/** Trigger a browser download for a blob the API returned. */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

/** Human-readable message for any thrown value. */
export function errorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (error instanceof ApiRequestError) return error.message
  if (error instanceof Error) return error.message
  return fallback
}
